import { useState, useEffect } from 'react'
import { XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/20/solid'
import { getSettings, saveSettings } from '@/lib/db'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isDismissed, setIsDismissed] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const [hasUserInteracted, setHasUserInteracted] = useState(false)

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true)
      return
    }

    // Check if user previously dismissed
    getSettings().then((settings) => {
      if (settings.hasInstalled) {
        setIsInstalled(true)
        return
      }
      if (settings.installPromptShownAt) {
        setIsDismissed(true)
      }
    })

    // Listen for user interaction (recording saved or note sent)
    const handleUserInteraction = () => {
      setHasUserInteracted(true)
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      const promptEvent = e as BeforeInstallPromptEvent
      setDeferredPrompt(promptEvent)
    }

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true)
      setDeferredPrompt(null)
      saveSettings({
        installPromptShownAt: new Date().toISOString(),
        hasInstalled: true,
        dismissedToasts: [],
      })
    }

    window.addEventListener('recording-saved', handleUserInteraction)
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener('recording-saved', handleUserInteraction)
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  const handleInstall = async () => {
    if (!deferredPrompt) return

    try {
      // Show install prompt
      await deferredPrompt.prompt()
      const choiceResult = await deferredPrompt.userChoice

      if (choiceResult.outcome === 'accepted') {
        setIsInstalled(true)
        await saveSettings({
          installPromptShownAt: new Date().toISOString(),
          hasInstalled: true,
          dismissedToasts: [],
        })
      } else {
        // User dismissed - remember for this session
        await saveSettings({
          installPromptShownAt: new Date().toISOString(),
          hasInstalled: false,
          dismissedToasts: [],
        })
      }

      setDeferredPrompt(null)
    } catch (error) {
      console.error('Error showing install prompt:', error)
    }
  }

  const handleDismiss = async () => {
    setIsDismissed(true)
    await saveSettings({
      installPromptShownAt: new Date().toISOString(),
      hasInstalled: false,
      dismissedToasts: [],
    })
  }

  // Don't show if installed, dismissed, no prompt available, or user hasn't interacted yet
  if (isInstalled || isDismissed || !deferredPrompt || !hasUserInteracted) {
    return null
  }

  return (
    <div
      className="bg-neon-cyan/20 border border-neon-cyan/30 rounded-xl p-4 mb-4"
      role="region"
      aria-label="Install app prompt"
    >
      <div className="flex items-start gap-3">
        <ArrowDownTrayIcon className="h-5 w-5 text-neon-cyan flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-semibold mb-1">Install App</h3>
          <p className="text-gray-300 text-sm mb-3">
            Install this app on your device for quick access and offline use. Works great on mobile!
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleInstall}
              className="px-4 py-2 rounded-lg bg-neon-cyan text-navy-dark font-semibold hover:bg-neon-cyan/80 transition-colors focus:outline-none focus:ring-2 focus:ring-neon-cyan"
              aria-label="Install app"
            >
              Install
            </button>
            <button
              onClick={handleDismiss}
              className="px-4 py-2 rounded-lg bg-gray-600/20 text-gray-300 font-semibold hover:bg-gray-600/30 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
              aria-label="Dismiss install prompt"
            >
              Not Now
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="p-1 rounded-lg text-gray-400 hover:text-white hover:bg-gray-600/20 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
          aria-label="Close install prompt"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

